import type { OrgContext, Permission, Scope } from '@high-crm/shared';
import { scopeRank } from '@high-crm/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setCurrentOrg } from './api';
import { useAuth } from './auth';
import i18n from '../i18n';

export interface MeResponse {
  profile: { id: string; email: string; full_name: string | null; locale: string; avatar_url: string | null } | null;
  organizations: Array<{ id: string; name: string; slug: string; status: string; role_key: string; role_name: string; subscription_status: string; plan_name: string; trial_ends_at: string | null }>;
  platform_admin_role: string | null;
}

interface OrgApi {
  me: MeResponse | undefined;
  meLoading: boolean;
  orgId: string | null;
  ctx: OrgContext | undefined;
  ctxLoading: boolean;
  switchOrg(id: string | null): void;
  can(permission: Permission, minScope?: Scope): boolean;
  feature(key: string): boolean;
  refresh(): Promise<void>;
}

const OrgContextReact = createContext<OrgApi | null>(null);
const ORG_KEY = 'highcrm.org';

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem(ORG_KEY));

  const meQuery = useQuery({ queryKey: ['me', user?.id], queryFn: () => api.get<MeResponse>('/api/me'), enabled: !!user, staleTime: 60_000 });

  // Pick a valid workspace: the stored one if the user still belongs to it, else the first.
  useEffect(() => {
    const orgs = meQuery.data?.organizations;
    if (!orgs) return;
    const valid = orgs.find((o) => o.id === orgId) ?? orgs[0];
    if ((valid?.id ?? null) !== orgId) setOrgId(valid?.id ?? null);
  }, [meQuery.data, orgId]);

  useEffect(() => {
    setCurrentOrg(orgId);
    if (orgId) localStorage.setItem(ORG_KEY, orgId);
    else localStorage.removeItem(ORG_KEY);
  }, [orgId]);

  const ctxQuery = useQuery({
    queryKey: ['org-context', orgId],
    queryFn: () => api.get<OrgContext>('/api/organization'),
    enabled: !!user && !!orgId,
    staleTime: 30_000,
  });

  // Language: user's profile locale wins, then the organisation default.
  useEffect(() => {
    const lang = localStorage.getItem('highcrm.lang') ?? meQuery.data?.profile?.locale ?? ctxQuery.data?.settings.locale;
    if (lang && lang !== i18n.language) void i18n.changeLanguage(lang);
  }, [meQuery.data?.profile?.locale, ctxQuery.data?.settings.locale]);

  // White-label colour
  useEffect(() => {
    const color = ctxQuery.data?.branding.primary_color;
    document.documentElement.style.setProperty('--brand', color ?? '#2f54eb');
  }, [ctxQuery.data?.branding.primary_color]);

  const switchOrg = useCallback((id: string | null) => {
    setCurrentOrg(id);
    setOrgId(id);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  }, [qc]);

  const value = useMemo<OrgApi>(() => ({
    me: meQuery.data,
    meLoading: meQuery.isLoading,
    orgId,
    ctx: ctxQuery.data,
    ctxLoading: ctxQuery.isLoading,
    switchOrg,
    can: (permission, minScope = 'own') => scopeRank(ctxQuery.data?.permissions[permission]) >= scopeRank(minScope),
    feature: (key) => Boolean(ctxQuery.data?.features[key] ?? (key in (ctxQuery.data?.limits ?? {}) && ctxQuery.data?.limits[key] !== 0)),
    refresh: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['me'] }), qc.invalidateQueries({ queryKey: ['org-context'] })]);
    },
  }), [meQuery.data, meQuery.isLoading, orgId, ctxQuery.data, ctxQuery.isLoading, switchOrg, qc]);

  return <OrgContextReact.Provider value={value}>{children}</OrgContextReact.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContextReact);
  if (!ctx) throw new Error('useOrg must be used inside OrgProvider');
  return ctx;
}
